import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { availableProviders, renderGoogleButton } from '@/lib/socialAuth';
import { DASHBOARD_ROUTE } from '@/lib/SignedOutOnly';
import { BTN_PRIMARY } from '@/components/bento';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Check } from 'lucide-react';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// ─── Field ────────────────────────────────────────────────────────────────────

function Field({ id, label, type, value, onChange, placeholder, icon: Icon, rightSlot, autoComplete, error, inputRef }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <label htmlFor={id} className="block text-[13px] font-medium text-zinc-300">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <input
          id={id}
          ref={inputRef}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          required
          className={`w-full bg-zinc-950 border rounded-xl py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 transition-all ${
            Icon ? 'pl-10' : 'pl-3.5'
          } ${rightSlot ? 'pr-10' : 'pr-3.5'} ${
            error
              ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/30'
              : 'border-zinc-800 focus:border-orange-500 focus:ring-orange-500/30'
          }`}
        />
        {rightSlot && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} className="text-[12px] text-red-400 leading-snug">{error}</p>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose                  receives the next open state
 * @param {string}   [props.description]            optional line under the title
 * @param {Function} [props.onAwaitingEmailConfirm] signup needs an emailed link
 * @param {'signin'|'signup'} [props.initialTab]    which form it opens on
 */
export default function AuthModal({ isOpen, onClose, description, onAwaitingEmailConfirm, initialTab = 'signin' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(initialTab);         // 'signin' | 'signup'

  // Each opening starts on the form the button promised: "Start free" opens
  // Sign Up, "Sign in" opens Sign In — not whichever tab was left open last.
  useEffect(() => {
    if (isOpen) setTab(initialTab);
  }, [isOpen, initialTab]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('idle');       // idle | loading | error | check_email | welcome
  const [errorMsg, setErrorMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [greetName, setGreetName] = useState('');
  const [providers, setProviders] = useState([]);

  const googleSlotRef = useRef(null);
  const firstFieldRef = useRef(null);

  const isSignup = tab === 'signup';
  const isLoading = status === 'loading';

  // Reset whenever the modal opens or the tab changes.
  useEffect(() => {
    setFirstName(''); setLastName(''); setEmail(''); setPassword('');
    setShowPassword(false); setStatus('idle'); setErrorMsg(''); setFieldErrors({});
  }, [isOpen, tab]);

  // Focus the first field so keyboard and screen-reader users land in the form.
  useEffect(() => {
    if (!isOpen || status !== 'idle') return;
    const t = setTimeout(() => firstFieldRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [isOpen, tab, status]);

  // Only offer providers this deployment can actually honour.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    availableProviders().then(list => { if (!cancelled) setProviders(list); });
    return () => { cancelled = true; };
  }, [isOpen]);

  /** Greet, then hand control back — the caller closes and resumes what they were doing. */
  // Where a successful sign-in lands: the dashboard, for everyone.
  //
  // It is what a returning user came back for, and the marketing page they
  // happened to sign in from is not — SignedOutOnly now redirects them off `/`
  // anyway, so leaving them there was never going to hold.
  //
  // A brand-new account used to be sent to a bare /build instead, on the
  // reasoning that a dashboard listing zero sites is a bleak first screen.
  // That route has no prompt and no site id, so Workspace bounced it to `/` and
  // the real destination was the marketing hero all along. The dashboard grew
  // its own "What do you wanna build?" composer since, which is the same offer
  // without the pitch wrapped around it, so first-timers go there too — and by
  // the shortest path rather than through two redirects.
  //
  // Nobody is moved out of a workspace they are already in. Signing in from
  // /build (a session that expired mid-build, say) must not throw away the
  // thing on screen, so any route already under /build is left alone.
  //
  // The same goes for someone who signed in to BUY something: on the pricing
  // page or a marketplace listing, the sign-in was a step on the way to a
  // checkout, and sending them to the dashboard made them find the plan or the
  // site all over again.
  const goAfterAuth = useCallback(() => {
    const here = (location.pathname || '').toLowerCase();
    // /admin too: a Telegram alert's link opens there, often in a browser
    // that is not signed in, and the listing it points at is the destination.
    if (['/build', '/preview', '/marketplace', '/pricing', '/admin'].some(p => here.startsWith(p))) return;
    navigate(DASHBOARD_ROUTE);
  }, [navigate, location.pathname]);

  const celebrate = useCallback((name) => {
    setGreetName(name || '');
    setStatus('welcome');
    setTimeout(() => {
      onClose(false);
      goAfterAuth();
    }, name ? 1900 : 900);
  }, [onClose, goAfterAuth]);

  // A password sign-in fires SIGNED_IN from elsewhere too (another tab); close
  // quietly in that case rather than showing a welcome for something the user
  // did not just do here.
  useEffect(() => {
    if (!isOpen) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' && status !== 'welcome' && status !== 'loading') onClose(false);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, onClose, status]);

  const completeSocial = useCallback(async (payload) => {
    setStatus('loading'); setErrorMsg('');
    const { data, error } = await supabase.auth.signInWithIdToken(payload);
    if (error) { setStatus('error'); setErrorMsg(error.message); return; }
    const full = data?.user?.user_metadata?.full_name || '';
    celebrate(data?.isNewUser ? full.split(' ')[0] : '');
  }, [celebrate]);

  // Google renders its own button, so it has to be mounted into a real node.
  useEffect(() => {
    if (!isOpen || status !== 'idle' || !providers.includes('google') || !GOOGLE_CLIENT_ID) return;
    const slot = googleSlotRef.current;
    if (!slot) return;
    renderGoogleButton(slot, {
      clientId: GOOGLE_CLIENT_ID,
      width: slot.offsetWidth,
      onCredential: completeSocial,
      onError: (err) => { setStatus('error'); setErrorMsg(err.message); },
    });
  }, [isOpen, status, tab, providers, completeSocial]);

  const validate = () => {
    const errs = {};
    if (isSignup) {
      if (!firstName.trim()) errs.firstName = 'Required';
      if (!lastName.trim())  errs.lastName = 'Required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Enter a valid email address';
    if (password.length < 6) errs.password = 'At least 6 characters';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      if (!isSignup) {
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (error) throw error;
        celebrate('');
        return;
      }

      const given = firstName.trim();
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { full_name: `${given} ${lastName.trim()}`.trim() } },
      });
      if (error) throw error;

      if (!data.session) {
        setStatus('check_email');
        onAwaitingEmailConfirm?.();
        return;
      }
      celebrate(given);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  const socialAvailable = providers.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[440px] bg-zinc-900 border-zinc-800 text-white p-0 overflow-hidden rounded-3xl sm:rounded-3xl">
        <AnimatePresence mode="wait">

          {/* ── Welcome ─────────────────────────────────────────────────── */}
          {status === 'welcome' ? (
            <motion.div
              key="welcome"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="px-6 py-14 text-center"
            >
              <DialogTitle className="sr-only">Signed in</DialogTitle>
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                className="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-5"
              >
                <Check className="w-7 h-7 text-ink" strokeWidth={3} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-2xl font-bold tracking-tight"
              >
                {greetName ? <>Welcome, {greetName}!</> : 'Welcome back'}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.22 }}
                className="text-zinc-400 text-sm mt-2"
              >
                {greetName ? 'Your account is ready. Let’s build something.' : 'Good to see you again.'}
              </motion.p>
            </motion.div>

          /* ── Check your email ──────────────────────────────────────────── */
          ) : status === 'check_email' ? (
            <motion.div key="check_email" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="px-6 py-12 text-center space-y-3">
              <DialogTitle className="sr-only">Confirm your email</DialogTitle>
              <div className="w-14 h-14 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Check your email</h3>
              <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
                We’ve sent a confirmation link to <span className="text-white font-medium">{email}</span>.
                Click it to activate your account.
              </p>
              <button
                onClick={() => { setStatus('idle'); setTab('signin'); }}
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors pt-1"
              >
                Back to sign in
              </button>
            </motion.div>

          /* ── The form ──────────────────────────────────────────────────── */
          ) : (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <DialogHeader className="pt-8 px-6 pb-5">
                <DialogTitle className="text-[22px] font-bold text-center tracking-tight">
                  {isSignup ? 'Create your account' : 'Welcome back'}
                </DialogTitle>
                <p className="text-sm text-zinc-400 text-center leading-relaxed pt-1.5">
                  {description || (isSignup
                    // No credit count here: this line once said 30 while every
                    // new account received 20. If a number comes back, read it
                    // from plans.json (FREE_MESSAGE_CREDITS), never a literal.
                    ? `Start building in under a minute.`
                    : 'Sign in to pick up where you left off.')}
                </p>
              </DialogHeader>

              <div className="px-6 pb-8 space-y-5">
                {/* Tabs */}
                <div className="flex bg-zinc-950 border border-zinc-800 rounded-full p-1">
                  {['signin', 'signup'].map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTab(t)}
                      className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all ${
                        tab === t ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      {t === 'signin' ? 'Sign In' : 'Sign Up'}
                    </button>
                  ))}
                </div>

                {/* Social — above the form, where people look first */}
                {socialAvailable && (
                  <div className="space-y-2.5">
                    {providers.includes('google') && (
                      <div ref={googleSlotRef} className="w-full flex justify-center min-h-[40px] [color-scheme:light]" />
                    )}
                    <div className="flex items-center gap-3 pt-1">
                      <span className="h-px flex-1 bg-zinc-800" />
                      <span className="text-[11px] uppercase tracking-widest text-zinc-600">or</span>
                      <span className="h-px flex-1 bg-zinc-800" />
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  {/* Name — signup only, and first, because it is the easiest
                      thing to answer and sets a friendly tone. */}
                  <AnimatePresence initial={false}>
                    {isSignup && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-2 gap-3">
                          <Field
                            id="first-name" label="First name" type="text"
                            value={firstName} onChange={v => setFirstName(v)}
                            placeholder="John" icon={User} autoComplete="given-name"
                            error={fieldErrors.firstName} inputRef={firstFieldRef}
                          />
                          <Field
                            id="last-name" label="Last name" type="text"
                            value={lastName} onChange={v => setLastName(v)}
                            placeholder="Doe" autoComplete="family-name"
                            error={fieldErrors.lastName}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Field
                    id="email" label="Email address" type="email"
                    value={email} onChange={setEmail}
                    placeholder="you@example.com" icon={Mail}
                    autoComplete="email" error={fieldErrors.email}
                    inputRef={isSignup ? undefined : firstFieldRef}
                  />

                  <Field
                    id="password" label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password} onChange={setPassword}
                    placeholder={isSignup ? 'At least 6 characters' : '••••••••'}
                    icon={Lock}
                    autoComplete={isSignup ? 'new-password' : 'current-password'}
                    error={fieldErrors.password}
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="text-zinc-500 hover:text-zinc-300 transition-colors"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    }
                  />

                  {!isSignup && (
                    <div className="flex justify-end -mt-1">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!email.trim()) {
                            setFieldErrors({ email: 'Enter your email above first' });
                            return;
                          }
                          await supabase.auth.resetPasswordForEmail(email.trim(), {
                            redirectTo: `${window.location.origin}/settings`,
                          });
                          setStatus('error');
                          setErrorMsg(`Password reset link sent to ${email.trim()}.`);
                        }}
                        className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  <AnimatePresence>
                    {status === 'error' && errorMsg && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        role="alert"
                        className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5"
                      >
                        {errorMsg}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full h-11 font-bold rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed group ${BTN_PRIMARY}`}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        {isSignup ? 'Creating account…' : 'Signing in…'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {isSignup ? 'Create account' : 'Sign in'}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    )}
                  </Button>

                  {isSignup && (
                    <p className="text-[11.5px] text-zinc-600 text-center leading-relaxed pt-0.5">
                      By creating an account you agree to our{' '}
                      <a href="/TermsOfUse" className="text-zinc-500 hover:text-zinc-300 underline underline-offset-2">Terms</a>
                      {' '}and{' '}
                      <a href="/PrivacyPolicy" className="text-zinc-500 hover:text-zinc-300 underline underline-offset-2">Privacy Policy</a>.
                    </p>
                  )}
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
